import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import {
    BuilderFormSelectDialog,
    BuilderScheduleSelectDialog,
} from "@/features/builder/components/BuilderSelectDialogs";
import { filterBuilderPickerItems } from "@/features/builder/utils/builderAccess";
import { useSelectFormsLite } from "@/features/forms/api/selectForms";
import { useSchedulesQuery } from "@/features/schedules/api/selectSchedules";
import { useTemplates } from "@/features/templates/api/selectTemplates";
import PatientSearch from "@/features/patients/features/overview/table/components/Search";
import { TemplatesMultiSelectDialog } from "@/features/templates/components/TemplatesMultiSelectDialog";
import { useAuth } from "@/utils/auth";
import { useForm } from "@/utils/form";
import { useNav } from "@/utils/nav";
import type { ExpandedSchedule } from "@patient-watch/types";
import { ui } from "@patient-watch/ui";
import { createFileRoute } from "@tanstack/react-router";
import { useState, type ReactNode } from "react";

export const Route = createFileRoute("/_app/design/components/select-dialogs")({
    component: SelectDialogsDemo,
});

function DemoSection({ title, description, children }: { title: string; description: string; children: ReactNode }) {
    return (
        <section className="space-y-4 pt-2 border-t">
            <div className="space-y-1">
                <h2 className="font-semibold text-lg">{title}</h2>
                <p className="text-muted-foreground text-sm">{description}</p>
            </div>
            <div className="space-y-6">{children}</div>
        </section>
    );
}

function Demo({ name, usage, value, children }: { name: string; usage: string; value: unknown; children: ReactNode }) {
    return (
        <div className="space-y-2">
            <div>
                <p className="font-medium text-sm">{name}</p>
                <p className="text-muted-foreground text-xs">{usage}</p>
            </div>
            {children}
            <pre className="bg-slate-50 dark:bg-slate-900 px-3 py-2 rounded-md overflow-x-auto font-mono text-2xs text-slate-600 dark:text-slate-300">
                {JSON.stringify(value ?? null)}
            </pre>
        </div>
    );
}

function SelectDialogsDemo() {
    useNav([
        { label: "Design", route: { to: "/design" } },
        { label: "Select dialogs", route: { to: "/design/components/select-dialogs" } },
    ]);

    const auth = useAuth();
    const isAdmin = Boolean(auth.roles?.isAdmin || auth.isSuperUser);
    const { data: forms = [] } = useSelectFormsLite();
    const { data: schedules = [] } = useSchedulesQuery();
    const { data: templates, isPending: templatesLoading } = useTemplates();

    const form = useForm<{ form_id: string; schedule_id: string }>({
        defaultValues: { form_id: "", schedule_id: "" },
    });
    const pickerValues = form.watch();
    const [templateIds, setTemplateIds] = useState<string[]>([]);

    const formOptions = filterBuilderPickerItems(
        forms.filter((f) => !!f.form_id),
        { isAdmin: auth.roles.isAdmin, userId: auth.id }
    ).map((f) => ({
        form_id: f.form_id as string,
        form_name: f.form_name ?? "",
        slug: f.slug ?? undefined,
        prevalence: f.prevalence ?? undefined,
        orthopaedic_areas: f.orthopaedic_areas ?? undefined,
        is_published: f.is_published ?? undefined,
        author: f.author ?? undefined,
    }));

    const scheduleOptions = filterBuilderPickerItems(
        schedules.filter((s) => !!s.schedule_id),
        { isAdmin: auth.roles.isAdmin, userId: auth.id }
    ).map((s) => ({ ...s, schedule_id: s.schedule_id as string })) as ExpandedSchedule[];

    return (
        <AdminPageLayout
            title="Select dialogs"
            description="Fields that open a dialog to choose one or more records. Use these instead of building a new picker dialog. For typing to find a match, see Search."
            maxWidthClassName="max-w-2xl"
        >
            <div className="space-y-8">
                <ui.Form {...form}>
                    <DemoSection
                        title="Select dialogs"
                        description="A field that opens a dialog with search, sorting and rich rows. Use when people browse and compare before choosing, such as a questionnaire for a tracker."
                    >
                        <Demo
                            name="BuilderFormSelectDialog"
                            usage="One questionnaire. Shows question count, prevalence and orthopaedic areas."
                            value={pickerValues.form_id}
                        >
                            <BuilderFormSelectDialog
                                name="form_id"
                                control={form.control}
                                options={formOptions}
                                label="Questionnaire"
                            />
                        </Demo>
                        <Demo
                            name="BuilderScheduleSelectDialog"
                            usage="One schedule. Shows the timings in each schedule."
                            value={pickerValues.schedule_id}
                        >
                            <BuilderScheduleSelectDialog
                                name="schedule_id"
                                control={form.control}
                                options={scheduleOptions}
                                label="Schedule"
                            />
                        </Demo>
                        <Demo
                            name="TemplatesMultiSelectDialog"
                            usage="Several templates, listed as cards below the field once picked. With showOwner, each card ends with the template's owner under a divider (admins only here)."
                            value={templateIds}
                        >
                            <TemplatesMultiSelectDialog
                                label="Templates"
                                placeholder="Select templates"
                                value={templateIds}
                                onValueChange={setTemplateIds}
                                templates={templates}
                                templatesLoading={templatesLoading}
                                showOwner={isAdmin}
                            />
                        </Demo>
                        <Demo
                            name="Patient search"
                            usage="A button that opens a search dialog, as in the patients list header. Picking a patient opens their record rather than filling a field."
                            value={null}
                        >
                            <PatientSearch />
                        </Demo>
                    </DemoSection>
                </ui.Form>
            </div>
        </AdminPageLayout>
    );
}
